import { cn } from "@/lib/utils";
import logoLight from "@/assets/claira-logo.png";
import logoDark from "@/assets/claira-logo-dark.png";
import lockupLight from "@/assets/claira-lockup.png";
import lockupDark from "@/assets/claira-lockup-dark.png";
import mark from "@/assets/claira-mark.png";

type Size = "sm" | "md" | "lg" | "xl";
type Variant = "main" | "lockup";

// Heights for the vertical "main" logo (arch + CLAIRA + tagline stacked).
const MAIN_HEIGHTS: Record<Size, string> = {
  sm: "h-10",
  md: "h-14",
  lg: "h-24",
  xl: "h-32 md:h-40",
};

// Heights for the horizontal "lockup" (arch beside CLAIRA + tagline).
const LOCKUP_HEIGHTS: Record<Size, string> = {
  sm: "h-7",
  md: "h-10",
  lg: "h-14",
  xl: "h-20 md:h-24",
};

const MARK_SIZES: Record<Size, string> = {
  sm: "h-6 w-6",
  md: "h-8 w-8",
  lg: "h-12 w-12",
  xl: "h-20 w-20 md:h-24 md:w-24",
};

/**
 * Claira brand lockup.
 * - `mark="only"` → arch glyph alone
 * - `variant="lockup"` → horizontal lockup (used in app chrome)
 * - default → vertical main logo (used on splash / loading screens)
 * Light/dark variants swap automatically.
 */
export function Wordmark({
  className,
  size = "md",
  mark: markMode = "none",
  variant = "main",
}: {
  className?: string;
  size?: Size;
  mark?: "left" | "only" | "none";
  variant?: Variant;
}) {
  if (markMode === "only") {
    return (
      <span role="img" aria-label="Claira" className={cn("inline-flex", className)}>
        <img
          src={mark}
          alt=""
          aria-hidden="true"
          className={cn(MARK_SIZES[size], "shrink-0 object-contain select-none")}
          draggable={false}
        />
      </span>
    );
  }

  const heights = variant === "lockup" ? LOCKUP_HEIGHTS : MAIN_HEIGHTS;
  const light = variant === "lockup" ? lockupLight : logoLight;
  const dark = variant === "lockup" ? lockupDark : logoDark;

  return (
    <span
      role="img"
      aria-label="CLAIRA — Mental Health Screening"
      className={cn("claira-wordmark inline-flex items-center", className)}
    >
      <img
        src={light}
        alt=""
        aria-hidden="true"
        className={cn(heights[size], "w-auto object-contain select-none block dark:hidden")}
        draggable={false}
      />
      <img
        src={dark}
        alt=""
        aria-hidden="true"
        className={cn(heights[size], "w-auto object-contain select-none hidden dark:block")}
        draggable={false}
      />
    </span>
  );
}
